import {
	applyPercentEdit,
	getDrainOrder,
	isAllocationTotalWithinLimit,
} from "@/shared/utils/allocationSplit";
import { useState } from "react";
import {
	BillPeriod,
	findOverlappingPeriod,
	formatBillingDate,
} from "../../../../../shared/utils/billPeriods";
import type { WaterBillGridRow } from "../../../utils/billRows";

const getSplitError = (
	row: WaterBillGridRow,
	targetCodes: string[],
): string | null => {
	if (targetCodes.length === 0) return null;

	const total = targetCodes.reduce(
		(sum, code) => sum + (row.percentByTarget[code] ?? 0),
		0,
	);
	return isAllocationTotalWithinLimit(total)
		? null
		: `% to Mill cannot go over 100 (now ${total.toLocaleString(undefined, { maximumFractionDigits: 2 })})`;
};

const getPeriodError = (
	periods: BillPeriod[],
	row: WaterBillGridRow,
): string | null => {
	const clash = findOverlappingPeriod(
		periods,
		row.servicePeriodStart,
		row.servicePeriodEnd,
		row.billId,
	);
	return clash === null
		? null
		: `Overlaps the bill of ${formatBillingDate(clash.start)} → ${formatBillingDate(clash.end)}`;
};

type UseBillEditingArgs = {
	rows: WaterBillGridRow[];
	draftRows: WaterBillGridRow[];
	setDraftRows: React.Dispatch<React.SetStateAction<WaterBillGridRow[]>>;
	targetCodes: string[];
	periods: BillPeriod[];
	editingBillId: number | null;
	setEditingBillId: (billId: number | null) => void;
	onBillSave: (rows: WaterBillGridRow[]) => void;
};

export type BillEditing = ReturnType<typeof useBillEditing>;

export const useBillEditing = ({
	rows,
	draftRows,
	setDraftRows,
	targetCodes,
	periods,
	editingBillId,
	setEditingBillId,
	onBillSave,
}: UseBillEditingArgs) => {
	const [percentOrder, setPercentOrder] = useState<string[]>([]);
	const [pending, setPending] = useState<{ billId: number | null } | null>(
		null,
	);
	const lastPercentCode = percentOrder[percentOrder.length - 1] ?? null;

	const patchRow = (id: string, patch: Partial<WaterBillGridRow>) =>
		setDraftRows((current) =>
			current.map((row) => (row.id === id ? { ...row, ...patch } : row)),
		);

	const patchPercent = (
		row: WaterBillGridRow,
		code: string,
		value: number | null,
	) => {
		const nextOrder = [...percentOrder.filter((other) => other !== code), code];
		setPercentOrder(nextOrder);
		patchRow(row.id, {
			percentByTarget: applyPercentEdit(
				row.percentByTarget,
				targetCodes,
				code,
				value,
				nextOrder,
			),
		});
	};

	const openSession = (billId: number | null) => {
		setPercentOrder([]);
		setPending(null);
		setEditingBillId(billId);
	};

	const billRowsOf = (source: WaterBillGridRow[], billId: number) =>
		source.filter((row) => row.billId === billId);

	const isDirty =
		editingBillId !== null &&
		JSON.stringify(billRowsOf(draftRows, editingBillId)) !==
			JSON.stringify(billRowsOf(rows, editingBillId));

	const startEdit = (billId: number) => {
		setDraftRows(rows);
		openSession(billId);
	};

	const cancelEdit = () => {
		setDraftRows(rows);
		openSession(null);
	};

	return {
		patchRow,
		patchPercent,

		isEditing: (billId: number) => editingBillId === billId,

		isLocked: (billId: number) =>
			editingBillId !== null && editingBillId !== billId,

		isPeriodDirty: (billId: number) => {
			if (editingBillId !== billId) return false;

			const findHeader = (source: WaterBillGridRow[]) =>
				source.find((row) => row.billId === billId && row.isFirstOfBill);
			const saved = findHeader(rows);
			const draft = findHeader(draftRows);
			if (saved === undefined || draft === undefined) return false;

			return (
				saved.servicePeriodStart?.getTime() !==
					draft.servicePeriodStart?.getTime() ||
				saved.servicePeriodEnd?.getTime() !== draft.servicePeriodEnd?.getTime()
			);
		},

		balancingCode:
			lastPercentCode === null
				? null
				: (getDrainOrder(targetCodes, lastPercentCode, percentOrder)[0] ??
					null),

		getError: (row: WaterBillGridRow) =>
			editingBillId !== row.billId || !row.isFirstOfBill
				? null
				: (getPeriodError(periods, row) ?? getSplitError(row, targetCodes)),

		isConfirmingDiscard: pending !== null,

		requestEdit: (billId: number) => {
			if (isDirty) setPending({ billId });
			else startEdit(billId);
		},

		requestCancelEdit: () => {
			if (isDirty) setPending({ billId: null });
			else cancelEdit();
		},

		resolvePending: (discard: boolean) => {
			if (!discard || pending === null) return setPending(null);
			if (pending.billId === null) cancelEdit();
			else startEdit(pending.billId);
		},

		confirmEdit: (billId: number) => {
			openSession(null);
			onBillSave(draftRows.filter((row) => row.billId === billId));
		},
	};
};
